import React, { useRef, useState } from "react";
import {
  DEFAULT_CROP_RECT,
  DEFAULT_IMAGE_FRAME,
  clampCropInsideImage,
  clampImageToCoverCrop,
  hostCropAspectRatio,
  moveCropRectInsideImage,
  moveImageFrame,
  normalizeHostCropFrame,
  normalizeImageFrame,
  resizeCropRectInsideImage,
  resizeImageFrameFromHandle,
} from "../../../../components/shared/imageToolbar/imageCropModel";
import WatermarkLogoMarkMedia from "./WatermarkLogoMarkMedia";
import "./WatermarkLogoCropOverlay.css";

const CORNERS = ["nw", "ne", "sw", "se"];

const pctBox = (frame) => ({
  left: `${frame.x * 100}%`,
  top: `${frame.y * 100}%`,
  width: `${frame.w * 100}%`,
  height: `${frame.h * 100}%`,
});

/**
 * Canva-style crop: logo is the outer canvas; crop window stays nested inside it.
 * Bright inside crop, dimmed logo outside crop; both movable but never separated.
 */
const WatermarkLogoCropOverlay = ({
  logoUrl,
  cropFrame = DEFAULT_CROP_RECT,
  imageFrame = DEFAULT_IMAGE_FRAME,
  cropAspect = "custom",
  naturalWidth = 0,
  naturalHeight = 0,
  width,
  height,
  tint = null,
  effect = "none",
  effectColor = "#FFFFFF",
  onFramesChange = () => {},
  onDone = () => {},
  onCancel = () => {},
  disabled = false,
}) => {
  const hostRef = useRef(null);
  const dragRef = useRef(null);
  const [showActions, setShowActions] = useState(false);

  const image = normalizeImageFrame(imageFrame);
  const crop = clampCropInsideImage(
    normalizeHostCropFrame(cropFrame),
    image
  );
  const aspectRatio = hostCropAspectRatio(
    cropAspect,
    naturalWidth,
    naturalHeight
  );

  const emitFrames = (nextCrop, nextImage) => {
    const img = clampImageToCoverCrop(
      normalizeImageFrame(nextImage),
      nextCrop
    );
    const c = clampCropInsideImage(normalizeHostCropFrame(nextCrop), img);
    onFramesChange(c, img);
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || disabled) return;
    const host = hostRef.current;
    if (!host) return;
    const bounds = host.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const dx = (e.clientX - drag.startClientX) / bounds.width;
    const dy = (e.clientY - drag.startClientY) / bounds.height;

    if (drag.type === "crop-move") {
      emitFrames(
        moveCropRectInsideImage(drag.startCrop, dx, dy, drag.startImage),
        drag.startImage
      );
    } else if (drag.type === "crop-resize") {
      emitFrames(
        resizeCropRectInsideImage(
          drag.startCrop,
          drag.handle,
          dx,
          dy,
          aspectRatio,
          drag.startImage
        ),
        drag.startImage
      );
    } else if (drag.type === "image-move") {
      emitFrames(
        drag.startCrop,
        moveImageFrame(drag.startImage, dx, dy, drag.startCrop)
      );
    } else if (drag.type === "image-resize") {
      emitFrames(
        drag.startCrop,
        resizeImageFrameFromHandle(
          drag.startImage,
          drag.handle,
          dx,
          dy,
          drag.startCrop
        )
      );
    }
  };

  const onPointerUp = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    endDrag();
  };

  const startDrag = (type, e, handle = "") => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (type === "crop-move" || type === "crop-resize") {
      setShowActions(true);
    } else {
      setShowActions(false);
    }
    dragRef.current = {
      type,
      handle,
      startCrop: { ...crop },
      startImage: { ...image },
      startClientX: e.clientX,
      startClientY: e.clientY,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  };

  // Crop position relative to the logo frame (for dim hole).
  const cropInImage = {
    x: (crop.x - image.x) / image.w,
    y: (crop.y - image.y) / image.h,
    w: crop.w / image.w,
    h: crop.h / image.h,
  };

  const imgNode = (
    <WatermarkLogoMarkMedia
      logoUrl={logoUrl}
      tint={tint}
      effect={effect}
      effectColor={effectColor}
      className="watermark-logo-crop__img"
    />
  );

  return (
    <div
      className="watermark-logo-crop-wrap"
      style={{ width }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        ref={hostRef}
        className="watermark-logo-crop"
        style={{ width, height }}
      >
        {/* Logo layer: full image + dim only outside crop (clipped to logo). */}
        <div
          className="watermark-logo-crop__image-layer"
          style={pctBox(image)}
        >
          {imgNode}
          <div
            className="watermark-logo-crop__dim"
            style={pctBox(cropInImage)}
            aria-hidden="true"
          />
        </div>

        {/* Outer logo boundary + circular handles (pan / zoom logo). */}
        <div
          className="watermark-logo-crop__image-boundary"
          style={pctBox(image)}
          onPointerDown={(e) => {
            setShowActions(false);
            startDrag("image-move", e);
          }}
        >
          {CORNERS.map((corner) => (
            <span
              key={`img-${corner}`}
              className={`watermark-logo-crop__image-handle watermark-logo-crop__image-handle--${corner}`}
              onPointerDown={(e) => startDrag("image-resize", e, corner)}
            />
          ))}
        </div>

        {/* Crop window + L-handles (always nested inside logo). */}
        <div
          className="watermark-logo-crop__crop-layer"
          style={pctBox(crop)}
          onPointerDown={(e) => {
            if (e.target.closest(".watermark-logo-crop__actions")) return;
            startDrag("crop-move", e);
          }}
        >
          {showActions ? (
            <div
              className="watermark-logo-crop__actions"
              role="group"
              aria-label="Crop actions"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="watermark-logo-crop__btn watermark-logo-crop__btn--cancel"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="watermark-logo-crop__btn watermark-logo-crop__btn--done"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onDone();
                }}
              >
                Done
              </button>
            </div>
          ) : null}
          {CORNERS.map((corner) => (
            <span
              key={`crop-${corner}`}
              className={`watermark-logo-crop__handle watermark-logo-crop__handle--${corner}`}
              onPointerDown={(e) => startDrag("crop-resize", e, corner)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WatermarkLogoCropOverlay;
